import dynamic from "next/dynamic";
import { memo, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useTheme } from "@mui/material";

import { useMediaQueryBreakpoint, usePerformance } from "@hooks";

const Spline = dynamic(
  () =>
    import("@baseComponents/SplineWrapper").then(mod => ({
      default: mod.SplineWrapper,
    })),
  {
    ssr: false,
    loading: () => <div />,
  },
);

import {
  CopyBlockContent,
  CopyBlockStyledProps,
  CopyBlockStyled,
  DescriptionStyled,
  LowerCorderBorder,
  NumberStyled,
  TitleStyled,
  UpperCorderBorder,
  FixedCopyContainer,
  PinDisabledLightStyled,
  PinDisabledDarkStyled,
  PinLightStyled,
  PinDarkStyled,
} from "./CopyBlock.styled";

interface CopyBlockProps extends CopyBlockStyledProps {
  index: number;
  enabled: boolean;
  inFocus?: number;
  header: string;
  body: string;
}

const CopyBlock = memo(
  ({ index, enabled, inFocus, header, body, ...props }: CopyBlockProps) => {
    const { splineSupport, isMounted } = usePerformance();
    const { isTablet } = useMediaQueryBreakpoint();
    const {
      palette: { mode },
    } = useTheme();
    const isDark = mode !== "light";

    const [isVisible, setIsVisible] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        },
        {
          threshold: 0.1,
          rootMargin: "50px",
        },
      );

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => {
        observer.disconnect();
      };
    }, []);

    return (
      <CopyBlockStyled {...props} enabled={enabled} ref={containerRef}>
        {splineSupport && isVisible && (
          <Spline
            style={{
              position: "absolute",
              bottom: "40px",
              width: "300px",
              height: "380px",
              left: "-80px",
              opacity: enabled ? 1 : 0,
              transition: "opacity 1s",
            }}
            onLoad={scene => scene.setZoom(1.5)}
            scene="/landing/sparcs.splinecode"
          />
        )}

        {!isDark && <PinDisabledLightStyled enabled={!enabled} />}
        {isDark && <PinDisabledDarkStyled enabled={!enabled} />}
        {!isDark && <PinLightStyled enabled={enabled} />}
        {isDark && <PinDarkStyled enabled={enabled} />}

        <CopyBlockContent enabled={enabled}>
          <NumberStyled enabled={enabled}>{index}</NumberStyled>
          {isTablet && (
            <>
              <TitleStyled enabled={enabled}>{header}</TitleStyled>
              <DescriptionStyled enabled={enabled}>
                <UpperCorderBorder />
                {body}
                <LowerCorderBorder />
              </DescriptionStyled>
            </>
          )}
          {!isTablet &&
            inFocus === index &&
            isMounted &&
            createPortal(
              <FixedCopyContainer>
                <TitleStyled enabled={true}>{header}</TitleStyled>
                <DescriptionStyled enabled={true}>{body}</DescriptionStyled>
              </FixedCopyContainer>,
              document.body,
            )}
        </CopyBlockContent>
      </CopyBlockStyled>
    );
  },
);

CopyBlock.displayName = "CopyBlock";
export { CopyBlock };
